import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ApiClientError } from "@/lib/api-client";
import { useAds, useAvailableAdAccounts, useSelectAdAccount } from "@/hooks/use-ads";
import { Button } from "@/components/ui/button";
import { PlatformIcon } from "@/components/ui/platform-icon";

interface AdAccountSelectorProps {
  brandId: string;
  brandSlug: string;
}

/**
 * Shows the brand's configured ad account and lets an admin pick one.
 * The available-accounts endpoint round-trips the ads provider serially per
 * connected account — it is only fetched once the picker is opened (lazy).
 */
export function AdAccountSelector({ brandId, brandSlug }: AdAccountSelectorProps) {
  const { t } = useTranslation("ads");
  const [picking, setPicking] = useState(false);
  const ads = useAds(brandId);
  const available = useAvailableAdAccounts(brandId, picking);
  const select = useSelectAdAccount(brandId);

  const account = ads.data?.account ?? null;
  const providerAuthError =
    available.error instanceof ApiClientError && available.error.code === "PROVIDER_AUTH";

  function choose(socialAccountId: string, platformAdAccountId: string) {
    select.mutate(
      { socialAccountId, platformAdAccountId },
      {
        onSuccess: () => {
          toast.success(t("adAccount.updatedToast"));
          setPicking(false);
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : t("adAccount.loadError"));
        },
      },
    );
  }

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">{t("adAccount.label")}</label>
      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground">
          {account
            ? `${account.name ?? t("adAccount.unnamed")} — ${account.platformAdAccountId} (${account.currency})`
            : t("adAccount.notConfigured")}
        </span>
        {!picking && (
          <Button size="sm" variant="outline" onClick={() => setPicking(true)}>
            {t("adAccount.chooseButton")}
          </Button>
        )}
      </div>

      {picking && (
        <div className="rounded-md border p-3 space-y-3">
          {available.isLoading && (
            <p className="text-sm text-muted-foreground">{t("adAccount.loading")}</p>
          )}
          {available.isError &&
            (providerAuthError ? (
              <p className="text-sm text-amber-400">
                {t("adAccount.connectFirst")}{" "}
                <Link
                  to={`/${brandSlug}/settings/accounts`}
                  className="underline hover:text-foreground"
                >
                  {t("adAccount.connectLink")}
                </Link>{" "}
                {t("adAccount.connectSuffix")}
              </p>
            ) : (
              <p className="text-sm text-red-400">
                {available.error instanceof Error
                  ? available.error.message
                  : t("adAccount.loadError")}
              </p>
            ))}
          {available.data &&
            (available.data.length === 0 ||
            available.data.every((g) => g.adAccounts.length === 0) ? (
              <p className="text-sm text-muted-foreground">
                {t("adAccount.noAccountsFound")}
              </p>
            ) : (
              available.data.map((group) => (
                <div key={group.socialAccountId} className="space-y-1.5">
                  <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    <PlatformIcon platform={group.platform} size={14} />
                    {group.platform}
                  </div>
                  {group.adAccounts.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      disabled={select.isPending}
                      onClick={() => choose(group.socialAccountId, a.id)}
                      className="flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm hover:bg-accent disabled:opacity-50"
                    >
                      <span>
                        {a.name ?? t("adAccount.unnamed")}{" "}
                        <span className="text-muted-foreground">({a.id})</span>
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {a.currency}
                        {a.currency !== "USD" && ` ${t("adAccount.nonUsdWarning")}`}
                      </span>
                    </button>
                  ))}
                </div>
              ))
            ))}
          <Button size="sm" variant="ghost" onClick={() => setPicking(false)}>
            {t("adAccount.closeButton")}
          </Button>
        </div>
      )}
    </div>
  );
}
